import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AgentInput,
  AgentRun,
  ParkingAgentService,
  agentError,
} from 'services/ParkingAgentService';

/** Server-owned jobs survive navigation. Requests from an old site never update the new site. */
export function useAgentRun<T>(
  siteId: number,
  kind: AgentInput['kind'],
  filename?: string,
) {
  const [run, setRun] = useState<AgentRun<T> | null>(null);
  const [history, setHistory] = useState<AgentRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const invalidate = useCallback(() => {
    ++generation.current;
  }, []);

  useEffect(() => {
    const version = ++generation.current;
    const controller = new AbortController();
    setRun(null);
    setHistory([]);
    setError('');
    setLoading(true);
    setStarting(false);
    ParkingAgentService.list(siteId, controller.signal)
      .then(async (all) => {
        const items = all.filter(
          (item) =>
            item.kind === kind &&
            (!filename || item.input.filename === filename),
        );
        if (version !== generation.current) return;
        setHistory(items);
        if (items[0]) {
          const latest = await ParkingAgentService.get<T>(
            siteId,
            items[0].id,
            controller.signal,
          );
          if (version === generation.current) setRun(latest);
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted && version === generation.current)
          setError(agentError(e));
      })
      .finally(() => {
        if (version === generation.current) setLoading(false);
      });
    return () => {
      invalidate();
      controller.abort();
    };
  }, [siteId, kind, filename, invalidate]);

  const select = useCallback(
    async (id: string) => {
      const version = ++generation.current;
      setLoading(true);
      setError('');
      try {
        const result = await ParkingAgentService.get<T>(siteId, id);
        if (generation.current === version) setRun(result);
      } catch (e) {
        if (generation.current === version) setError(agentError(e));
      } finally {
        if (generation.current === version) setLoading(false);
      }
    },
    [siteId],
  );

  const start = useCallback(
    async (input: AgentInput) => {
      const version = ++generation.current;
      setStarting(true);
      setLoading(false);
      setError('');
      try {
        const result = await ParkingAgentService.start<T>(siteId, input);
        if (generation.current === version) {
          setRun(result);
          setHistory((items) => [result, ...items].slice(0, 30));
        }
      } catch (e) {
        if (generation.current === version) setError(agentError(e));
      } finally {
        if (generation.current === version) setStarting(false);
      }
    },
    [siteId],
  );

  const running = run?.status === 'queued' || run?.status === 'running';
  useEffect(() => {
    if (!running || !run) return;
    const controller = new AbortController();
    let pending = false;
    const timer = window.setInterval(async () => {
      if (pending) return;
      pending = true;
      try {
        const next = await ParkingAgentService.get<T>(
          siteId,
          run.id,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          setRun(next);
          setError('');
        }
      } catch (e) {
        if (!controller.signal.aborted) setError(agentError(e));
      } finally {
        pending = false;
      }
    }, 2000);
    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [siteId, run?.id, running]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    run,
    history,
    loading,
    busy: starting || running,
    error,
    start,
    select,
  };
}
